import {
  AspectRatio,
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
  VStack,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Progress,
} from '@chakra-ui/react'

import { motion } from 'framer-motion'
import { useState } from 'react'
import UploadSvg from '../assets/upload.svg'
import { ethers } from 'ethers'
import Papa from 'papaparse'

const ValidEth = () => {
  const [file, setFile] = useState(null)
  const [validAddress, setValidAddress] = useState<Array<string>>([])
  const [invalidAddress, setInvalidAddress] = useState<Array<string>>([])
  const [stats, setStats] = useState({ total: 0, distinct: 0, valid: 0, invalid: 0 })

  const handleChange = (event: any) => {
    setFile(event.target.files[0])
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    if (!file) {
      return
    }
    Papa.parse(file, {
      complete: function (results: any) {
        let allAddresses = results.data.map((addr: any) =>
          String(Object.values(addr)[0]).toString().trim()
        )
        let distinctAddress = allAddresses.filter((v: any, i: any, a: any) => a.indexOf(v) === i)
        let validAddresses = distinctAddress.filter((addr: string) => ethers.utils.isAddress(addr))
        let invalidAddresses = distinctAddress.filter(
          (addr: string) => !ethers.utils.isAddress(addr)
        )
        setValidAddress(validAddresses)
        setInvalidAddress(invalidAddresses)
        setStats({
          total: allAddresses.length,
          distinct: distinctAddress.length,
          valid: validAddresses.length,
          invalid: invalidAddresses.length,
        })
      },
    })
  }

  return (
    <VStack
      spacing={{ base: '30px' }}
      minH={{ base: '60vh', md: '60vh' }}
      wrap={'wrap'}
      align={'center'}
      justify={'center'}
      mb={'10px'}
    >
      <HStack
        minH={{ base: '70vh', md: '67vh' }}
        wrap={'wrap'}
        align={'center'}
        justify={'center'}
        mb={'10px'}
      >
        <Image src={UploadSvg} boxSize={{ base: '220px', md: 'md' }}></Image>
        <Stack
          spacing={4}
          bg={useColorModeValue('white', 'gray.700')}
          rounded={'xl'}
          boxShadow={'lg'}
          p={{ base: '12', md: '12' }}
          my={{ base: '6', md: '12' }}
        >
          <Heading fontSize={{ base: '4xl', md: '4xl' }} color={'brand.500'} alignSelf={'center'}>
            Valid_ETH
          </Heading>
          <Text alignSelf={'center'}>Validates Ethereum Addresses</Text>
          <AspectRatio width="72" ratio={2}>
            <Box
              borderColor="gray.300"
              borderStyle="dashed"
              borderWidth="2px"
              rounded="md"
              shadow="sm"
              role="group"
              transition="all 150ms ease-in-out"
              _hover={{
                shadow: 'md',
              }}
              as={motion.div}
              initial="rest"
              animate="rest"
              whileHover="hover"
            >
              <Box position="relative" width="100%">
                <Stack p="4" textAlign="center" spacing="1">
                  <Heading fontSize="lg" color="gray.700" fontWeight="bold">
                    Drag and Drop CSV File
                  </Heading>
                  <Text fontWeight="light">or click to upload</Text>
                </Stack>
                <Input
                  type="file"
                  width="100%"
                  height={'32'}
                  position="absolute"
                  top="0"
                  left="0"
                  opacity="0"
                  aria-hidden="true"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleChange}
                />
              </Box>
            </Box>
          </AspectRatio>
          {file && (
            <Stack>
              <Text>File Name: {file['name']}</Text>
            </Stack>
          )}
          <Stack spacing={6}>
            {file && (
              <Button
                bg={'brand.500'}
                color={'white'}
                _disabled={{
                  bg: 'gray.600',
                  cursor: 'not-allowed',
                }}
                _hover={{
                  bg: 'brand.600',
                }}
                onClick={handleSubmit}
                isDisabled={!file}
              >
                Validate
              </Button>
            )}
            {stats && stats.distinct && (
              <>
                <Heading mb={'5px'} fontSize={'xl'}>
                  Total: {stats.distinct} Addresses
                </Heading>
                <Stat>
                  <StatNumber mb={'5px'}>
                    <StatLabel>
                      {`${stats.valid}`}
                      <Text float={'right'}>
                        {`${((stats.valid * 100) / stats.distinct).toFixed(2)}%`}
                      </Text>
                    </StatLabel>
                    <Progress
                      value={(stats.valid * 100) / stats.distinct}
                      size="lg"
                      colorScheme="teal"
                      borderRadius={'10px'}
                    />
                  </StatNumber>
                  <StatNumber mb={'5px'}>
                    <StatLabel>
                      {`${stats.invalid}`}
                      <Text float={'right'}>{`${((stats.invalid * 100) / stats.distinct).toFixed(
                        2
                      )}%`}</Text>
                    </StatLabel>
                    <Progress
                      value={(stats.invalid * 100) / stats.distinct}
                      size="lg"
                      colorScheme="red"
                      borderRadius={'10px'}
                    />
                  </StatNumber>
                </Stat>
              </>
            )}
          </Stack>
        </Stack>
      </HStack>
      {(!!validAddress.length || !!invalidAddress.length) && (
        <HStack alignItems={'flex-start'} gap={'30px'}>
          {!!validAddress.length && (
            <Stack>
              <TableContainer boxShadow={'lg'}>
                <Table size="md" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Valid Addresses</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {validAddress.map((addr: string) => {
                      return (
                        <Tr key={addr}>
                          <Td>{addr}</Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Stack>
          )}
          {!!invalidAddress.length && (
            <Stack>
              <TableContainer boxShadow={'lg'}>
                <Table size="md" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Invalid Addresses</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {invalidAddress.map((addr: string) => {
                      return (
                        <Tr key={addr}>
                          <Td>{addr}</Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </HStack>
      )}
    </VStack>
  )
}

export default ValidEth
